import { useState } from "react";
import { Alert, Badge, Button, Card, Field, Input, Modal, Switch } from "../../ds";
import type { DeviceDetail, NetworkInterface, NetworkStatus, NetworkDevice } from "../../lib/api";
import { fmtUptime } from "../../lib/format";
import { SectionCard } from "../SectionCard";
import { KeyValues } from "../KeyValues";
import { useDeviceAction } from "../../lib/hooks/useDeviceAction";
import { useSection } from "../../lib/hooks/useSection";

// Sektion „Netzwerk“ → actions `network_get` / `network_set`.
//
// Interfaces samt ihrem DHCP-Pool: „im LAN kein DHCP, im Debug-WLAN schon“ ist
// genau eine Zeile je Interface. Gelöscht wird nur ausdrücklich (`remove`) —
// eine vergessene Zeile darf hier kein Interface kosten.

const PROTOCOLS = ["static", "dhcp", "none", "pppoe"];

interface IfaceForm {
  /** Vorhandenes Interface; fehlt beim Anlegen. */
  id?: string;
  name: string;
  proto: string;
  device: string;
  ipaddr: string;
  netmask: string;
  gateway: string;
  dns: string;
  mtu: string;
  metric: string;
  disabled: boolean;
  dhcpEnabled: boolean;
  dhcpStart: string;
  dhcpLimit: string;
  dhcpLeasetime: string;
}

function toIfaceForm(iface: NetworkInterface): IfaceForm {
  return {
    id: iface.id,
    name: iface.id,
    proto: iface.proto ?? "static",
    device: iface.device ?? "",
    ipaddr: iface.ipaddr ?? "",
    netmask: iface.netmask ?? "",
    gateway: iface.gateway ?? "",
    dns: (iface.dns ?? []).join(", "),
    mtu: iface.mtu !== undefined ? String(iface.mtu) : "",
    metric: iface.metric !== undefined ? String(iface.metric) : "",
    disabled: iface.disabled ?? false,
    dhcpEnabled: iface.dhcp?.enabled ?? false,
    dhcpStart: iface.dhcp?.start !== undefined ? String(iface.dhcp.start) : "100",
    dhcpLimit: iface.dhcp?.limit !== undefined ? String(iface.dhcp.limit) : "150",
    dhcpLeasetime: iface.dhcp?.leasetime ?? "12h",
  };
}

function emptyIfaceForm(): IfaceForm {
  return {
    name: "",
    proto: "static",
    device: "",
    ipaddr: "",
    netmask: "255.255.255.0",
    gateway: "",
    dns: "",
    mtu: "",
    metric: "",
    disabled: false,
    dhcpEnabled: true,
    dhcpStart: "100",
    dhcpLimit: "150",
    dhcpLeasetime: "12h",
  };
}

/** Formular → `network_set`-Params. Leere Textfelder bleiben weg, der Agent
 *  lässt sie damit unverändert. */
function toParams(form: IfaceForm): Record<string, unknown> {
  const iface: Record<string, unknown> = { proto: form.proto };
  if (form.id) iface.id = form.id;
  else iface.name = form.name.trim();
  const dns = form.dns
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (form.device.trim()) iface.device = form.device.trim();
  if (form.ipaddr.trim()) iface.ipaddr = form.ipaddr.trim();
  if (form.netmask.trim()) iface.netmask = form.netmask.trim();
  if (form.gateway.trim()) iface.gateway = form.gateway.trim();
  if (dns.length) iface.dns = dns;
  if (form.mtu.trim()) iface.mtu = Number(form.mtu);
  if (form.metric.trim()) iface.metric = Number(form.metric);
  iface.disabled = form.disabled;

  const pool: Record<string, unknown> = {
    interface: form.id ?? form.name.trim(),
    enabled: form.dhcpEnabled,
  };
  if (form.dhcpEnabled) {
    if (form.dhcpStart.trim()) pool.start = Number(form.dhcpStart);
    if (form.dhcpLimit.trim()) pool.limit = Number(form.dhcpLimit);
    if (form.dhcpLeasetime.trim()) pool.leasetime = form.dhcpLeasetime.trim();
  }
  return { interfaces: [iface], dhcp: [pool] };
}

/** Adressierung — bei `proto: dhcp` holt der Router sie selbst. */
function AddressFields({
  form,
  onChange,
}: {
  form: IfaceForm;
  onChange: (form: IfaceForm) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Name"
          helperText="uci-Sectionname, z.B. debug"
          value={form.name}
          disabled={!!form.id}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
        />
        <Field label="Protokoll" htmlFor="iface-proto">
          <select
            id="iface-proto"
            className="ds-input"
            value={form.proto}
            onChange={(e) => onChange({ ...form, proto: e.target.value })}
          >
            {PROTOCOLS.map((proto) => (
              <option key={proto} value={proto}>
                {proto}
              </option>
            ))}
          </select>
        </Field>
        <Input
          label="Gerät"
          helperText="z.B. br-lan, eth0.3"
          value={form.device}
          onChange={(e) => onChange({ ...form, device: e.target.value })}
        />
      </div>

      {form.proto === "static" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="IP-Adresse"
            value={form.ipaddr}
            onChange={(e) => onChange({ ...form, ipaddr: e.target.value })}
          />
          <Input
            label="Netzmaske"
            value={form.netmask}
            onChange={(e) => onChange({ ...form, netmask: e.target.value })}
          />
          <Input
            label="Gateway"
            value={form.gateway}
            onChange={(e) => onChange({ ...form, gateway: e.target.value })}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="DNS-Server"
          helperText="kommagetrennt"
          value={form.dns}
          onChange={(e) => onChange({ ...form, dns: e.target.value })}
        />
        <Input
          label="MTU"
          inputMode="numeric"
          value={form.mtu}
          onChange={(e) => onChange({ ...form, mtu: e.target.value })}
        />
        <Input
          label="Metrik"
          helperText="Kleiner gewinnt bei mehreren Uplinks."
          inputMode="numeric"
          value={form.metric}
          onChange={(e) => onChange({ ...form, metric: e.target.value })}
        />
      </div>
    </>
  );
}

/** Der DHCP-Pool gehört zum Interface — genau hier steht „im LAN aus, im
 *  Debug-WLAN an". */
function DhcpFields({ form, onChange }: { form: IfaceForm; onChange: (form: IfaceForm) => void }) {
  return (
    <Card title="DHCP-Server auf diesem Interface">
      <div className="flex flex-col gap-3">
        <Switch
          label="Adressen vergeben"
          checked={form.dhcpEnabled}
          onChange={(v) => onChange({ ...form, dhcpEnabled: v })}
        />
        {form.dhcpEnabled && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Erste Adresse (Offset)"
              helperText="100 = …1.100"
              inputMode="numeric"
              value={form.dhcpStart}
              onChange={(e) => onChange({ ...form, dhcpStart: e.target.value })}
            />
            <Input
              label="Anzahl"
              inputMode="numeric"
              value={form.dhcpLimit}
              onChange={(e) => onChange({ ...form, dhcpLimit: e.target.value })}
            />
            <Input
              label="Lease-Dauer"
              helperText="z.B. 12h, 30m, infinite"
              value={form.dhcpLeasetime}
              onChange={(e) => onChange({ ...form, dhcpLeasetime: e.target.value })}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

function InterfaceModal({
  form,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  form: IfaceForm | null;
  busy: boolean;
  onChange: (form: IfaceForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (form === null) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? `Interface ${form.id}` : "Interface anlegen"}
      subtitle="Der Mgmt-Kanal läuft über LTE — ein Fehler hier sperrt niemanden aus."
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            loading={busy}
            disabled={!form.id && !form.name.trim()}
            onClick={onSubmit}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <AddressFields form={form} onChange={onChange} />
        <DhcpFields form={form} onChange={onChange} />
        <Switch
          label="Interface abschalten"
          checked={form.disabled}
          onChange={(v) => onChange({ ...form, disabled: v })}
        />
      </div>
    </Modal>
  );
}

function dhcpBadge(iface: NetworkInterface) {
  if (!iface.dhcp) return <Badge variant="neutral">kein DHCP</Badge>;
  return iface.dhcp.enabled ? (
    <Badge variant="success">
      DHCP .{iface.dhcp.start ?? "?"}–{(iface.dhcp.start ?? 0) + (iface.dhcp.limit ?? 0) - 1}
    </Badge>
  ) : (
    <Badge variant="warn">DHCP aus</Badge>
  );
}

function InterfaceCard({
  iface,
  locked,
  onEdit,
  onRemove,
}: {
  iface: NetworkInterface;
  locked: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const removable = iface.id !== "lan" && iface.id !== "wan";
  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {iface.id}
          <Badge variant={iface.up ? "success" : "neutral"} dot>
            {iface.up ? "up" : "down"}
          </Badge>
          {iface.disabled && <Badge variant="warn">deaktiviert</Badge>}
          {dhcpBadge(iface)}
        </span>
      }
      subtitle={`${iface.proto ?? "?"} · ${iface.device ?? "kein Gerät"}${
        iface.uptimeSecs !== undefined ? ` · seit ${fmtUptime(iface.uptimeSecs)}` : ""
      }`}
      actions={
        <>
          <Button size="sm" variant="outline" iconName="edit" disabled={locked} onClick={onEdit}>
            Bearbeiten
          </Button>
          {removable && (
            <Button size="sm" variant="ghost" iconName="trash" disabled={locked} onClick={onRemove}>
              Entfernen
            </Button>
          )}
        </>
      }
    >
      <KeyValues
        items={[
          ["Adressen", (iface.addresses ?? []).join(", ") || "—"],
          ["Konfiguriert", iface.ipaddr ? `${iface.ipaddr} / ${iface.netmask ?? "?"}` : "—"],
          ["Gateway", iface.gateway ?? "—"],
          ["DNS", (iface.dns ?? []).join(", ") || "—"],
          ["MTU / Metrik", `${iface.mtu ?? "—"} / ${iface.metric ?? "—"}`],
          [
            "DHCP",
            iface.dhcp
              ? `${iface.dhcp.enabled ? "an" : "aus"} · Start ${iface.dhcp.start ?? "—"} · ${
                  iface.dhcp.limit ?? "—"
                } Adressen · ${iface.dhcp.leasetime ?? "—"}`
              : "kein Pool eingerichtet",
          ],
        ]}
      />
    </Card>
  );
}

// --- Geräte / Bridges (`config device`) ---

const DEVICE_TYPES = ["bridge", "8021q", "8021ad", "macvlan", "veth"];

interface DeviceForm {
  /** Anonyme uci-Section; fehlt beim Anlegen. */
  id?: string;
  name: string;
  type: string;
  ports: string; // komma-/leerzeichengetrennt
  macaddr: string;
  mtu: string;
  stp: boolean;
  igmpSnooping: boolean;
}

function toDeviceForm(d: NetworkDevice): DeviceForm {
  return {
    id: d.id,
    name: d.name ?? "",
    type: d.type ?? "bridge",
    ports: (d.ports ?? []).join(", "),
    macaddr: d.macaddr ?? "",
    mtu: d.mtu !== undefined ? String(d.mtu) : "",
    stp: d.stp ?? false,
    igmpSnooping: d.igmpSnooping ?? false,
  };
}

function emptyDeviceForm(): DeviceForm {
  return {
    name: "",
    type: "bridge",
    ports: "",
    macaddr: "",
    mtu: "",
    stp: false,
    igmpSnooping: false,
  };
}

/** Formular → `network_set`-Params für ein Gerät. */
function deviceToParams(form: DeviceForm): Record<string, unknown> {
  const dev: Record<string, unknown> = {};
  if (form.id) dev.id = form.id;
  else dev.name = form.name.trim();
  if (form.type) dev.type = form.type;
  const ports = form.ports
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (form.type === "bridge") {
    dev.ports = ports;
    if (!ports.length) dev.bridge_empty = true;
    dev.stp = form.stp;
    dev.igmp_snooping = form.igmpSnooping;
  } else if (ports.length) {
    dev.ports = ports;
  }
  if (form.macaddr.trim()) dev.macaddr = form.macaddr.trim();
  if (form.mtu.trim()) dev.mtu = Number(form.mtu);
  return { devices: [dev] };
}

function DeviceModal({
  form,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  form: DeviceForm | null;
  busy: boolean;
  onChange: (form: DeviceForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (form === null) return null;
  const bridge = form.type === "bridge";
  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? `Gerät ${form.name || form.id}` : "Gerät / Bridge anlegen"}
      subtitle="Ein `config device` — z.B. eine Bridge über mehrere Ports."
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            loading={busy}
            disabled={!form.id && !form.name.trim()}
            onClick={onSubmit}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="Name"
            helperText="Gerätename, z.B. br-lan"
            value={form.name}
            disabled={!!form.id}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
          />
          <Field label="Typ" htmlFor="dev-type">
            <select
              id="dev-type"
              className="ds-input"
              value={form.type}
              onChange={(e) => onChange({ ...form, type: e.target.value })}
            >
              {DEVICE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Input
            label="MTU"
            inputMode="numeric"
            value={form.mtu}
            onChange={(e) => onChange({ ...form, mtu: e.target.value })}
          />
        </div>
        <Input
          label={bridge ? "Ports" : "Basis-Gerät"}
          helperText={bridge ? "kommagetrennt, z.B. eth0.1, eth0.3" : "z.B. eth0"}
          value={form.ports}
          onChange={(e) => onChange({ ...form, ports: e.target.value })}
        />
        <Input
          label="MAC-Adresse"
          helperText="optional, überschreibt die des Ports"
          value={form.macaddr}
          onChange={(e) => onChange({ ...form, macaddr: e.target.value })}
        />
        {bridge && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Switch
              label="STP (Spanning Tree)"
              checked={form.stp}
              onChange={(v) => onChange({ ...form, stp: v })}
            />
            <Switch
              label="IGMP-Snooping"
              checked={form.igmpSnooping}
              onChange={(v) => onChange({ ...form, igmpSnooping: v })}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeviceCard({
  dev,
  locked,
  onEdit,
  onRemove,
}: {
  dev: NetworkDevice;
  locked: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {dev.name ?? dev.id}
          <Badge variant="neutral">{dev.type ?? "device"}</Badge>
          {dev.stp && <Badge variant="neutral">STP</Badge>}
        </span>
      }
      actions={
        <>
          <Button size="sm" variant="outline" iconName="edit" disabled={locked} onClick={onEdit}>
            Bearbeiten
          </Button>
          <Button size="sm" variant="ghost" iconName="trash" disabled={locked} onClick={onRemove}>
            Entfernen
          </Button>
        </>
      }
    >
      <KeyValues
        items={[
          ["Ports", (dev.ports ?? []).join(", ") || "—"],
          ["MAC", dev.macaddr ?? "—"],
          ["MTU", dev.mtu !== undefined ? String(dev.mtu) : "—"],
        ]}
      />
    </Card>
  );
}

// --- Globale Netzwerk-Settings (`config globals`) ---

interface GlobalsForm {
  ulaPrefix: string;
  packetSteering: boolean;
}

function GlobalsModal({
  form,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  form: GlobalsForm | null;
  busy: boolean;
  onChange: (form: GlobalsForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (form === null) return null;
  return (
    <Modal
      open
      onClose={onClose}
      title="Globale Netzwerk-Einstellungen"
      subtitle="Gilt für den ganzen Router."
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" iconName="check" loading={busy} onClick={onSubmit}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="ULA-Präfix"
          helperText="z.B. fdxx:xxxx:xxxx::/48"
          value={form.ulaPrefix}
          onChange={(e) => onChange({ ...form, ulaPrefix: e.target.value })}
        />
        <Switch
          label="Packet Steering"
          checked={form.packetSteering}
          onChange={(v) => onChange({ ...form, packetSteering: v })}
        />
      </div>
    </Modal>
  );
}

export function NetworkSection({ device }: { device: DeviceDetail }) {
  const section = useSection<NetworkStatus>(device, "network");
  const { busy, note, setNote, run } = useDeviceAction(device.id);
  const [form, setForm] = useState<IfaceForm | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<NetworkInterface | null>(null);
  const [deviceForm, setDeviceForm] = useState<DeviceForm | null>(null);
  const [confirmRemoveDevice, setConfirmRemoveDevice] = useState<NetworkDevice | null>(null);
  const [globalsForm, setGlobalsForm] = useState<GlobalsForm | null>(null);

  const locked = !device.online || busy !== null;

  async function send(params: Record<string, unknown>, okText: string) {
    const result = await run("network_set", params, okText);
    if (result) await section.refresh();
  }

  async function save() {
    const edited = form;
    setForm(null);
    if (!edited) return;
    await send(toParams(edited), edited.id ? "Interface geändert." : "Interface angelegt.");
  }

  async function remove() {
    const target = confirmRemove;
    setConfirmRemove(null);
    if (!target) return;
    await send({ remove: [target.id] }, `Interface ${target.id} entfernt.`);
  }

  async function saveDevice() {
    const edited = deviceForm;
    setDeviceForm(null);
    if (!edited) return;
    await send(deviceToParams(edited), edited.id ? "Gerät geändert." : "Gerät angelegt.");
  }

  async function removeDevice() {
    const target = confirmRemoveDevice;
    setConfirmRemoveDevice(null);
    if (!target) return;
    await send({ removeDevices: [target.id] }, `Gerät ${target.name ?? target.id} entfernt.`);
  }

  async function saveGlobals() {
    const edited = globalsForm;
    setGlobalsForm(null);
    if (!edited) return;
    await send(
      { globals: { ulaPrefix: edited.ulaPrefix.trim(), packetSteering: edited.packetSteering } },
      "Globale Einstellungen übernommen.",
    );
  }

  return (
    <>
      <SectionCard
        title="Netzwerk"
        description="Interfaces, DHCP je Interface, Bridges"
        section={section}
        deviceOnline={device.online}
        iconName="swap"
        above={
          note ? (
            <Alert variant={note.variant} dismissible onDismiss={() => setNote(null)}>
              {note.text}
            </Alert>
          ) : undefined
        }
      >
        {(data) => (
          <div className="flex flex-col gap-4">
            {(data.interfaces ?? []).map((iface) => (
              <InterfaceCard
                key={iface.id}
                iface={iface}
                locked={locked}
                onEdit={() => setForm(toIfaceForm(iface))}
                onRemove={() => setConfirmRemove(iface)}
              />
            ))}

            {(data.devices ?? []).map((dev) => (
              <DeviceCard
                key={dev.id}
                dev={dev}
                locked={locked}
                onEdit={() => setDeviceForm(toDeviceForm(dev))}
                onRemove={() => setConfirmRemoveDevice(dev)}
              />
            ))}

            <Card
              title="Global"
              subtitle="Gilt für den ganzen Router"
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  iconName="edit"
                  disabled={locked}
                  onClick={() =>
                    setGlobalsForm({
                      ulaPrefix: data.globals?.ulaPrefix ?? "",
                      packetSteering: data.globals?.packetSteering ?? false,
                    })
                  }
                >
                  Bearbeiten
                </Button>
              }
            >
              <KeyValues
                items={[
                  ["ULA-Präfix", data.globals?.ulaPrefix ?? "—"],
                  ["Packet Steering", data.globals?.packetSteering ? "an" : "aus"],
                ]}
              />
            </Card>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                iconName="plus"
                disabled={locked}
                onClick={() => setDeviceForm(emptyDeviceForm())}
              >
                Gerät / Bridge anlegen
              </Button>
              <Button
                variant="outline"
                iconName="plus"
                disabled={locked}
                onClick={() => setForm(emptyIfaceForm())}
              >
                Interface anlegen
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <InterfaceModal
        form={form}
        busy={busy === "network_set"}
        onChange={setForm}
        onClose={() => setForm(null)}
        onSubmit={() => void save()}
      />

      <DeviceModal
        form={deviceForm}
        busy={busy === "network_set"}
        onChange={setDeviceForm}
        onClose={() => setDeviceForm(null)}
        onSubmit={() => void saveDevice()}
      />

      <GlobalsModal
        form={globalsForm}
        busy={busy === "network_set"}
        onChange={setGlobalsForm}
        onClose={() => setGlobalsForm(null)}
        onSubmit={() => void saveGlobals()}
      />

      <Modal
        open={confirmRemoveDevice !== null}
        onClose={() => setConfirmRemoveDevice(null)}
        title={`Gerät ${confirmRemoveDevice?.name ?? confirmRemoveDevice?.id} entfernen?`}
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmRemoveDevice(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="trash"
              loading={busy === "network_set"}
              onClick={() => void removeDevice()}
            >
              Entfernen
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-2">
          Die `config device`-Section verschwindet. Interfaces, die dieses Gerät benutzen, stehen
          danach ohne Unterbau da — vorher die Interface-Zuordnung prüfen.
        </p>
      </Modal>

      <Modal
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
        title={`Interface ${confirmRemove?.id} entfernen?`}
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmRemove(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="trash"
              loading={busy === "network_set"}
              onClick={() => void remove()}
            >
              Entfernen
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-2">
          Das Interface und sein DHCP-Pool verschwinden vom Router. Geräte, die daran hängen,
          verlieren ihre Adresse. `lan` und `wan` lehnt der Router grundsätzlich ab.
        </p>
      </Modal>
    </>
  );
}
